import { z } from 'zod';

import {
  createFullNameSchema,
  createMobileSchema,
} from '@/app/lib/forms/schemas/base-fields.schema';
import {
  BASE_FORM_ERROR_MESSAGE,
  getErrorMessage,
} from '@/app/lib/forms/utils/messages';
import { validateThaiIdCard } from '@/app/lib/forms/utils/validations.function';

export const MAXIMUM_PASSPORT_LENGTH = 15;
export const CAMPAIGN_ID = 'special_deal_oct_2026';

export const PASSPORT_ERROR_MESSAGE = {
  MIN_LENGTH: {
    th: 'หนังสือเดินทางต้องมีอย่างน้อย 6 ตัวอักษร',
    en: 'Please enter a valid passport with at least 6 characters',
  },
  MAX_LENGTH: {
    th: 'หนังสือเดินทางต้องมีไม่เกิน 15 ตัวอักษร',
    en: 'Please enter a valid passport with at most 15 characters',
  },
  INVALID_FORMAT: {
    th: 'รูปแบบหนังสือเดินทางไม่ถูกต้อง',
    en: 'Please enter a valid passport',
  },
};
export const NATIONAL_ID_ERROR_MESSAGE = {
  REQUIRED: {
    th: 'กรุณากรอกเลขบัตรประชาชน',
    en: 'Please enter a valid national ID',
  },
  INVALID_FORMAT: {
    th: 'รูปแบบเลขบัตรประชาชนไม่ถูกต้อง',
    en: 'Please enter a valid national ID',
  },
};

export const createNationalIdSchema = (locale: 'th' | 'en' = 'th') =>
  z
    .string({
      error: getErrorMessage(NATIONAL_ID_ERROR_MESSAGE.REQUIRED, locale),
    })
    .length(
      13,
      getErrorMessage(NATIONAL_ID_ERROR_MESSAGE.INVALID_FORMAT, locale)
    )
    .refine((value) => /^\d{13}$/.test(value), {
      message: getErrorMessage(
        NATIONAL_ID_ERROR_MESSAGE.INVALID_FORMAT,
        locale
      ),
    })
    .refine((value) => validateThaiIdCard(value), {
      message: getErrorMessage(
        NATIONAL_ID_ERROR_MESSAGE.INVALID_FORMAT,
        locale
      ),
    });

export const createPassportSchema = (locale: 'th' | 'en' = 'th') =>
  z
    .string({
      error: getErrorMessage(BASE_FORM_ERROR_MESSAGE, locale),
    })
    .min(6, getErrorMessage(PASSPORT_ERROR_MESSAGE.MIN_LENGTH, locale))
    .max(
      MAXIMUM_PASSPORT_LENGTH,
      getErrorMessage(PASSPORT_ERROR_MESSAGE.MAX_LENGTH, locale)
    )
    .trim();

export const CitizenTypeEnum = z.enum(['national_id', 'passport']);
export type CitizenType = z.infer<typeof CitizenTypeEnum>;

export const RefChannelEnum = z.enum(['spin_assigned', 'spin_shared_link'], {
  error: 'Invalid reference channel.',
});
export type RefChannel = z.infer<typeof RefChannelEnum>;

export const PhoneNumberField = createMobileSchema('en');

export const FullNameField = createFullNameSchema('en');

export const CampaignField = z.string().optional();

export const TrackerIdField = z.string().min(1);

export const CampaignRequiredField = z.string().min(1, {
  message: 'Campaign is required.',
});

export const ModelCodeField = z.string().min(1, {
  message: 'Model code is required.',
});

export const EligibilityBaseSchema = z.object({
  phoneNumber: PhoneNumberField,
  fullName: createFullNameSchema('en').optional(),
  campaign: CampaignField,
  modelCode: ModelCodeField,
});

export const NationalIdEligibilitySchema = EligibilityBaseSchema.extend({
  type: z.literal('national_id'),
  citizenId: createNationalIdSchema('en'),
});

export const PassportEligibilitySchema = EligibilityBaseSchema.extend({
  type: z.literal('passport'),
  citizenId: createPassportSchema('en'),
});

export const EligibilitySchema = z.discriminatedUnion('type', [
  NationalIdEligibilitySchema,
  PassportEligibilitySchema,
]);

export type EligibilityInput = z.infer<typeof EligibilitySchema>;
export type EligibilityCitizenType = EligibilityInput['type'];

export const ConfirmLuckyDrawSchema = z.object({
  trackerId: TrackerIdField,
  modelCode: ModelCodeField,
});

export type ConfirmLuckyDrawInput = z.infer<typeof ConfirmLuckyDrawSchema>;

export const GetTrackerSchema = z.object({
  trackerId: TrackerIdField,
  campaign: CampaignRequiredField,
  modelCode: z.string().optional(),
});

export type GetTrackerInput = z.infer<typeof GetTrackerSchema>;

export const ClaimLuckyDrawSchema = z.object({
  refTrackerId: TrackerIdField,
  refChannel: RefChannelEnum,
  campaign: CampaignRequiredField,
  fullName: FullNameField,
  phoneNumber: PhoneNumberField,
  type: CitizenTypeEnum.optional(),
  citizenId: z.string().optional(),
  leadId: z.string().optional(),
  modelCode: ModelCodeField,
});

export type ClaimLuckyDrawInput = z.infer<typeof ClaimLuckyDrawSchema>;

export const DiscountPriceSchema = z.object({
  special: z.number(),
  oldCustomer: z.number(),
  otherBrand: z.number().optional(),
  total: z.number().optional(),
  multiply: z.number().optional(),
});

export type DiscountPriceType = z.infer<typeof DiscountPriceSchema>;

export const TrackerResultSchema = z.object({
  campaign: z.string(),
  trackerId: z.string(),
  leadId: z.string().optional().nullable(),
  qrCodeData: z.string(),
  fullName: z.string(),
  tier: z.string().nullable(),
  resultPrize: z.string(),
  modelCode: z.string().optional().nullable(),
  discounts: z.object({
    pickup: DiscountPriceSchema,
    ppv: DiscountPriceSchema,
  }),
});

export type TrackerResult = z.infer<typeof TrackerResultSchema>;

export const ClaimLuckyDrawResultSchema = z.object({
  campaign: z.string(),
  refTrackerId: z.string(),
  refQrCodeData: z.string(),
  refFullName: z.string(),
  refTier: z.string().nullable(),
  discounts: z.object({
    pickup: DiscountPriceSchema,
    ppv: DiscountPriceSchema,
  }),
});

export type ClaimLuckyDrawResult = z.infer<typeof ClaimLuckyDrawResultSchema>;

export const CampaignModelSchema = z.object({
  modelCode: z.string(),
  model: z.string(),
  modelGroup: z.string(),
});

export type CampaignModel = z.infer<typeof CampaignModelSchema>;

export const CampaignModelsResponseSchema = z.object({
  models: z.array(CampaignModelSchema),
});

export type CampaignModelsResponse = z.infer<
  typeof CampaignModelsResponseSchema
>;
